// The BI SPA reads Vite env and exposes the backend origin + the stub
// employee identity. All /api/bi/* traffic goes through the single typed
// client (src/lib/api.ts); this file only needs the base URL and the
// identity header value.
import type { PusherChannelConfig } from "@bi/shared";

import { effectiveEmployeeEmail } from "./lib/identity";

const env = import.meta.env;

// The employee email the SPA presents, as known AT MODULE LOAD. In cloud the
// real email is the Google `bi_authority` cookie's, which the SPA cannot read
// (HttpOnly) — it arrives with the boot identity probe (`employeeEmail` on
// `GET /api/bi/dashboards`) and is cached here for the NEXT load by
// lib/api.ts -> lib/identity.ts. So on a cold first load this is still the
// build-time stub, and anything that must be right on THIS load — above all
// the `private-bi-{email}` Pusher channel — reads the email off that response
// instead (see pages/dashboard.tsx). Locally the stub default flows through
// the `X-Employee-Email` header, which the backend's stub-mode auth honors.
// Empty string while SIGNED OUT so `effectiveEmployeeEmail` suppresses the
// header and log out actually ends the session.
export const EMPLOYEE_EMAIL =
  effectiveEmployeeEmail(env.VITE_DEV_EMPLOYEE_EMAIL ?? "employee@fiverr.com") ?? "";

// Backend origin. Empty in every cloud env (`VITE_API_BASE_URL: ""` in the
// deploy workflows): one LB serves `/api/*` from the BI service and everything
// else from the SPA bucket (terraform/modules/bi-service), so requests are
// SAME-ORIGIN and relative. Load-bearing: `bi_authority` is a SameSite=Lax
// host-only cookie and the OAuth callback redirects to the request's own host,
// so a cross-site BI origin breaks sign-in. Only local dev is cross-origin
// (:5377 -> :8200), where auth is the stub header, not the cookie.
export const BI_API_BASE = env.VITE_API_BASE_URL ?? "http://localhost:8200";

// The BI dashboard-query completion channel; mirrors the backend's `channel_for_bi`.
export function biChannel(email: string): string {
  return `private-bi-${email}`;
}

export const PUSHER_AUTH_ENDPOINT = `${BI_API_BASE}/api/bi/pusher/auth`;

/** Stub identity header the BI SPA sends alongside the Pusher auth POST
 *  (mirrors `send()`'s own header in lib/api.ts) — empty while signed out so
 *  the auth call fails closed rather than resubscribing under a stale email. */
function biAuthHeaders(): Record<string, string> {
  return EMPLOYEE_EMAIL ? { "x-employee-email": EMPLOYEE_EMAIL } : {};
}

/** Pusher transport config for the `biChannel` chart-settle channel.
 *  `idleDisconnectMs: 0` so a settle event is not dropped while the tab is
 *  briefly backgrounded mid-load. */
export function biPusherConfig(): PusherChannelConfig {
  return {
    key: env.VITE_PUSHER_KEY ?? "local-key",
    host: env.VITE_PUSHER_HOST ?? "localhost",
    port: Number(env.VITE_PUSHER_PORT ?? "6201"),
    useTls: (env.VITE_PUSHER_USE_TLS ?? "false") === "true",
    cluster: env.VITE_PUSHER_CLUSTER ?? "mt1",
    authEndpoint: PUSHER_AUTH_ENDPOINT,
    authHeaders: biAuthHeaders(),
    idleDisconnectMs: 0,
  };
}
